import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {
  Badge,
  Button,
  Link,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {mapCountryCodeToFlags} from '@/utils/countries';
import * as formatDate from '@/utils/date';

import MerchantListSummary from './MerchantListSummary';

export const MerchantTable = ({
  businesses,
}: {
  businesses: {business: AfriexBusiness; person: AfriexBusinessUser}[];
}) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20 px-4">&nbsp;</Th>
          <Th className="ps-10">Business Name</Th>
          <Th className="px-4">Country</Th>
          <Th className="px-4">Verified</Th>
          <Th className="px-4">Created At</Th>
          <Th className="px-4">&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {businesses.map((biz, index) => (
          <BusinessTableRow key={index} item={biz} index={index} />
        ))}
      </TBody>
    </Table>
  );
};
const BusinessTableRow = ({
  item,
  index,
}: {
  item: {business: AfriexBusiness; person: AfriexBusinessUser};
  index: number;
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [isExpanded, setIsExpanded] = useState(false);

  const zebraRow = index % 2 ? 'bg-gray-50 dark:bg-dark-zebraRow' : '';
  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <Button
            data-cy="merchant-business-table-dropdown-button"
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td
          data-cy="merchant-business-table-name"
          className="font-medium dark:text-gray-200 ps-10"
        >
          {item.business.name}
        </Td>
        <Td data-cy="merchant-business-table-country" className="px-4">
          {item?.business?.countryCode || 'N/A'}{' '}
          {
            mapCountryCodeToFlags(countryList)[
              item?.business?.countryCode || ''
            ]
          }
        </Td>
        <Td
          data-cy="merchant-business-table-verification-flag"
          className="px-4"
        >
          <Badge
            label={item.business.meta?.flags?.isVerified ? 'Yes' : 'No'}
            colorScheme={
              item.business.meta?.flags?.isVerified ? 'successful' : 'failed'
            }
            className="px-4"
          />
        </Td>
        <Td data-cy="merchant-business-table-timestamp" className="px-4">
          {formatDate.DDMonYYYY(item?.business?.createdAt?.toString() || '')}
        </Td>
        <Td>
          <Link
            to={`/merchants/businesses/${item.business.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="merchant-business-details-link"
          >
            <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500 cursor-pointer">
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr className="pr-5" enableHover={false}>
          <Td colspan={9} className="p-0 m-0">
            <div className="flex py-5 sm:py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <MerchantListSummary
                business={item.business}
                person={item.person}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};
